/**
 * LeagueChatPage — /league/:leagueId/chat
 * ─────────────────────────────────────────────────────────────────
 * League gap-closure Phase B (2026-06-09). The cross-team coaches'
 * channel: coaches/managers of enrolled teams + league staff talk
 * in one league-wide room (spare-finding, schedule swaps, "anyone
 * have a spare goalie tonight?").
 *
 * Access model mirrors the backend exactly (league-chat.js):
 *   • league manager/administrator OR coach/manager of an enrolled
 *     team can read + post;
 *   • league viewers and players get a friendly "coaches only" note;
 *   • the channel is OFF by default — a manager (or admin with
 *     manage_league_settings) sees an enable card instead of an
 *     error (the backend distinguishes "off" from "forbidden").
 *
 * Implementation notes:
 *   • 10s polling like the team ChatPage — no websocket dependency.
 *   • Soft-deleted messages render as a tombstone line.
 *   • The backend snapshots sender team; we render "Name · Team"
 *     (or "Name · League" for staff posts).
 *   • Reply UI intentionally NOT built in v1 — the schema + API
 *     accept reply_to already; thread UI can come later without a
 *     migration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, Send, Trash2, Shield } from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';

const POLL_MS = 10000;

export default function LeagueChatPage() {
  const { leagueId } = useParams();

  const [ctx, setCtx] = useState(null);          // /chat/context payload
  const [ctxLoading, setCtxLoading] = useState(true);
  const [messages, setMessages] = useState([]);  // oldest-first for display
  const [enabled, setEnabled] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);

  // ── Context probe ──
  useEffect(() => {
    let cancelled = false;
    setCtxLoading(true);
    api.get(`/leagues/${leagueId}/chat/context`)
      .then(({ data }) => { if (!cancelled) { setCtx(data); setEnabled(data?.coaches_channel_enabled === true); } })
      .catch(() => { if (!cancelled) setCtx(null); })
      .finally(() => { if (!cancelled) setCtxLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId]);

  // ── Messages: initial load + poll ──
  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/chat/coaches?limit=100`);
      if (data?.enabled === false) { setEnabled(false); return; }
      setEnabled(true);
      // API returns newest-first; display oldest-first.
      setMessages((data?.messages || []).slice().reverse());
    } catch {
      /* 403 for non-coaches is handled by the access gate below;
         transient errors just skip a poll tick. */
    }
  }, [leagueId]);

  useEffect(() => {
    if (!ctx?.can_access) return undefined;
    loadMessages();
    const t = setInterval(loadMessages, POLL_MS);
    return () => clearInterval(t);
  }, [ctx?.can_access, loadMessages]);

  // ── Autoscroll: stick to bottom unless the user scrolled up ──
  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);
  function onScroll() {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  async function send() {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/chat/coaches`, { content });
      if (data?.message) setMessages(prev => [...prev, data.message]);
      setDraft('');
      stickToBottomRef.current = true;
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not send');
    } finally {
      setSending(false);
    }
  }

  async function remove(id) {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/leagues/${leagueId}/chat/messages/${id}`);
      setMessages(prev => prev.map(m => m.id === id
        ? { ...m, deleted: true, content: null, can_delete: false } : m));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete');
    }
  }

  async function toggleChannel(next) {
    setToggling(true);
    try {
      const { data } = await api.put(`/leagues/${leagueId}/chat/settings`,
        { coaches_channel_enabled: next });
      setEnabled(data?.coaches_channel_enabled === true);
      toast.success(next ? 'Coaches channel enabled' : 'Coaches channel disabled');
      if (next) loadMessages();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update setting');
    } finally {
      setToggling(false);
    }
  }

  // ── Render ──
  const body = (() => {
    if (ctxLoading) {
      return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)' }}>Loading…</div>;
    }
    if (!ctx) {
      return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)' }}>Could not load chat.</div>;
    }
    if (!ctx.can_access) {
      return (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 14 }}>
          <Shield size={20} style={{ marginBottom: 8 }} />
          <div>The coaches channel is limited to team coaches and league staff.</div>
        </div>
      );
    }
    if (enabled === false) {
      return (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Coaches channel is off</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
            When enabled, coaches of every team in this league (plus league staff) share one
            channel for cross-team coordination — finding spares, swapping ice times, game-day logistics.
          </div>
          {ctx.can_toggle ? (
            <button className="btn-ice" disabled={toggling} onClick={() => toggleChannel(true)}>
              {toggling ? 'Enabling…' : 'Enable coaches channel'}
            </button>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
              A league manager can enable it from this page.
            </div>
          )}
        </div>
      );
    }
    return (
      // Mobile fix: was a fixed `70vh`, which does NOT shrink when the
      // Capacitor native keyboard opens (unlike the web viewport), so the
      // composer at the bottom of this card got hidden behind the keyboard
      // — the same "chat cut off" bug the team ChatPage already fixed via
      // var(--mobile-h). Reusing that variable here makes this card shrink
      // in lockstep with the visible area; desktop is unaffected since
      // --mobile-h is unset there and falls back to 100dvh (~= 70vh).
      <div className="card" style={{ display: 'flex', flexDirection: 'column', height: 'min(calc(var(--mobile-h, 100dvh) * 0.7), 640px)', padding: 0, overflow: 'hidden' }}>
        <div ref={listRef} onScroll={onScroll}
             style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.length === 0 && (
            <div style={{ margin: 'auto', color: 'var(--text-2)', fontSize: 13 }}>
              No messages yet — say hi to the other benches.
            </div>
          )}
          {messages.map(m => <MessageRow key={m.id} m={m} onDelete={remove} />)}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', padding: 10, display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={draft}
            maxLength={2000}
            placeholder={ctx.my_teams?.[0]?.team_name
              ? `Message as ${ctx.my_teams[0].team_name}…`
              : 'Message the league…'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg-2)',
              color: 'var(--text)', fontSize: 14, outline: 'none',
            }}
          />
          <button className="btn-ice" onClick={send} disabled={sending || !draft.trim()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Send size={15} /> Send
          </button>
        </div>
      </div>
    );
  })();

  return (
    <div style={{ paddingBottom: 60 }}>
      <LeagueSubNav league={null} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageSquare size={22} /> Coaches channel
          </h1>
          {enabled === true && ctx?.can_toggle && (
            <button className="btn-ghost" disabled={toggling} onClick={() => toggleChannel(false)}
                    style={{ fontSize: 12 }}>
              {toggling ? 'Working…' : 'Disable channel'}
            </button>
          )}
        </header>
        {body}
      </div>
    </div>
  );
}

function MessageRow({ m, onDelete }) {
  if (m.deleted) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic' }}>
        (message removed)
      </div>
    );
  }
  return (
    <div style={{
      alignSelf: m.is_mine ? 'flex-end' : 'flex-start',
      maxWidth: '78%',
      background: m.is_mine ? 'var(--ice-a10)' : 'var(--bg-2)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '8px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          {m.sender?.name || '(unknown)'}
        </span>
        <span style={{ fontSize: 11, color: m.sender?.is_league_staff ? '#b284ff' : 'var(--text-2)' }}>
          {m.sender?.is_league_staff ? 'League' : (m.sender?.team_name || '')}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 'auto' }}>
          {formatTime(m.created_at)}
        </span>
        {m.can_delete && (
          <button onClick={() => onDelete(m.id)} title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: 0 }}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {m.content}
      </div>
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}
